import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Bell,
  BellRing,
  Car,
  FileText,
  XCircle,
  CheckCheck,
  ExternalLink,
  RefreshCw,
  Inbox,
  Clock,
  ChevronRight,
  Settings,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAdminNotifications } from "@/hooks/use-admin-notifications";
import { useAdminNotificationBadge } from "@/contexts/AdminNotificationBadgeContext";
import {
  FormattedAdminNotification,
  AdminNotificationCategory,
} from "@/lib/services/admin-notification.service";
import { useTranslation } from "react-i18next";
import {
  requestNotificationPermission,
  getNotificationPermission,
} from "@/lib/services/browser-notification.service";

/**
 * Get icon for notification category
 */
function getCategoryIcon(category: AdminNotificationCategory) {
  switch (category) {
    case "vehicles":
      return Car;
    case "documents":
      return FileText;
    case "cancellations":
      return XCircle;
    default:
      return Bell;
  }
}

/**
 * Get color classes for notification category (Premium Emerald palette)
 */
function getCategoryColors(category: AdminNotificationCategory) {
  switch (category) {
    case "vehicles":
      return {
        bg: "bg-emerald-50 dark:bg-emerald-950/30",
        text: "text-emerald-600 dark:text-emerald-400",
        ring: "ring-emerald-500/20",
      };
    case "documents":
      return {
        bg: "bg-amber-50 dark:bg-amber-950/30",
        text: "text-amber-600 dark:text-amber-400",
        ring: "ring-amber-500/20",
      };
    case "cancellations":
      return {
        bg: "bg-rose-50 dark:bg-rose-950/30",
        text: "text-rose-600 dark:text-rose-400",
        ring: "ring-rose-500/20",
      };
    default:
      return {
        bg: "bg-gray-100 dark:bg-gray-800",
        text: "text-gray-600 dark:text-gray-400",
        ring: "ring-gray-500/20",
      };
  }
}

/**
 * Single notification item component - Airbnb style
 */
function NotificationItem({
  notification,
  onMarkAsRead,
  onNavigate,
  t,
}: {
  notification: FormattedAdminNotification;
  onMarkAsRead: (id: string) => void;
  onNavigate: (path: string) => void;
  t: (key: string) => string;
}) {
  const Icon = getCategoryIcon(notification.category);
  const colors = getCategoryColors(notification.category);

  const handleClick = useCallback(() => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    onNavigate(notification.actionLink);
  }, [notification.actionLink, notification.id, notification.isRead, onMarkAsRead, onNavigate]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  const handleMarkAsRead = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onMarkAsRead(notification.id);
    },
    [notification.id, onMarkAsRead]
  );

  return (
    <div
      className={cn(
        "group relative rounded-xl transition-all duration-200 cursor-pointer",
        "hover:bg-gray-50 dark:hover:bg-gray-800/50",
        !notification.isRead
          ? "bg-white dark:bg-gray-800/80"
          : "opacity-70 hover:opacity-100"
      )}
      role="button"
      tabIndex={0}
      aria-label={`${notification.title}. ${notification.actionText}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Unread indicator line */}
      {!notification.isRead && (
        <div className="absolute left-0 top-3 bottom-3 w-1 bg-emerald-500 rounded-full" />
      )}

      <div className="p-3.5 pl-4">
        <div className="flex gap-3.5">
          {/* Icon */}
          <div
            className={cn(
              "h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ring-1",
              "transition-transform duration-200 group-hover:scale-105",
              colors.bg,
              colors.text,
              colors.ring
            )}
          >
            <Icon className="h-5 w-5" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-0.5">
              <h4
                className={cn(
                  "text-[13px] font-semibold leading-tight line-clamp-1",
                  !notification.isRead
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-600 dark:text-gray-400"
                )}
              >
                {notification.title}
              </h4>

              <div className="flex items-center gap-1.5 shrink-0">
                {!notification.isRead && (
                  <button
                    onClick={handleMarkAsRead}
                    className={cn(
                      "p-1 rounded-md transition-colors",
                      "bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:hover:bg-emerald-800/60"
                    )}
                    title={t("admin.notificationsDropdown.markAsRead")}
                  >
                    <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                  </button>
                )}
              </div>
            </div>

            <p
              className={cn(
                "text-xs leading-relaxed line-clamp-2 mb-2",
                !notification.isRead
                  ? "text-gray-600 dark:text-gray-300"
                  : "text-gray-500 dark:text-gray-500"
              )}
            >
              {notification.message}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{notification.timeAgo}</span>
              </div>

              <span
                className={cn(
                  "text-[11px] font-medium flex items-center gap-0.5",
                  "text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
                  "opacity-0 group-hover:opacity-100 transition-all duration-200"
                )}
              >
                {notification.actionText}
                <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state component
 */
function EmptyState({ category }: { category: AdminNotificationCategory }) {
  const messages: Record<AdminNotificationCategory, string> = {
    all: "Tout est calme",
    vehicles: "Aucun véhicule en attente",
    documents: "Aucun document en attente",
    cancellations: "Aucune annulation",
  };

  const subMessages: Record<AdminNotificationCategory, string> = {
    all: "Pas de nouvelles notifications",
    vehicles: "Les demandes apparaîtront ici",
    documents: "Les documents à vérifier apparaîtront ici",
    cancellations: "Les demandes d'annulation apparaîtront ici",
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center h-full">
      <div className="p-5 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 mb-4">
        <Inbox className="h-10 w-10 text-gray-300 dark:text-gray-600" />
      </div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {messages[category]}
      </h3>
      <p className="text-xs text-gray-500 max-w-[180px]">{subMessages[category]}</p>
    </div>
  );
}

/**
 * Loading skeleton
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-2 p-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3.5 p-3.5 rounded-xl">
          <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * AdminNotificationDropdown - Premium Airbnb-inspired design
 */
export function AdminNotificationDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [browserNotifPermission, setBrowserNotifPermission] = useState(
    getNotificationPermission()
  );

  const { counts, isAdmin, refresh: refreshBadge } = useAdminNotificationBadge();
  const {
    notifications,
    loading,
    activeCategory,
    changeCategory,
    markAsRead,
    markAllAsRead,
    refresh,
  } = useAdminNotifications();

  const handleRequestNotificationPermission = useCallback(async () => {
    const permission = await requestNotificationPermission();
    setBrowserNotifPermission(permission);
    if (permission === "granted") {
      toast.success("Notifications navigateur activées ✅");
    }
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      setIsOpen(false);
      navigate(path);
    },
    [navigate]
  );

  const handleMarkAsRead = useCallback(
    async (id: string) => {
      await markAsRead(id);
      refreshBadge();
    },
    [markAsRead, refreshBadge]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
    refreshBadge();
  }, [markAllAsRead, refreshBadge]);

  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  const categories: {
    value: AdminNotificationCategory;
    label: string;
    count: number;
  }[] = [
      { value: "all", label: "Tout", count: counts.total },
      { value: "vehicles", label: "Véhicules", count: counts.vehicles },
      { value: "documents", label: "Docs", count: counts.documents },
      { value: "cancellations", label: "Annul.", count: counts.cancellations },
    ];

  if (!isAdmin) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "relative h-10 w-10 rounded-full transition-all duration-200 group",
            "hover:bg-gray-100 dark:hover:bg-gray-800",
            isOpen && "bg-gray-100 dark:bg-gray-800"
          )}
          aria-label={`Notifications admin${counts.total > 0 ? ` (${counts.total} non lues)` : ""
            }`}
        >
          {counts.total > 0 ? (
            <BellRing className="w-5 h-5 text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
          ) : (
            <Bell className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors" />
          )}

          {/* Badge - Airbnb red dot style */}
          {counts.total > 0 && (
            <span
              className={cn(
                "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px]",
                "flex items-center justify-center px-1",
                "bg-rose-500 text-white text-[10px] font-bold rounded-full",
                "border-2 border-white dark:border-gray-900",
                "animate-in zoom-in duration-200"
              )}
            >
              {counts.total > 9 ? "9+" : counts.total}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className={cn(
          "w-[380px] p-0 rounded-2xl overflow-hidden",
          "bg-white dark:bg-gray-900 shadow-2xl",
          "border border-gray-200 dark:border-gray-800",
          "animate-in slide-in-from-top-2 zoom-in-95 duration-200"
        )}
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-xl">
              <Bell className="h-4 w-4 text-gray-700 dark:text-gray-300" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                Notifications
              </h3>
              <p className="text-[10px] text-gray-500">Admin</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {browserNotifPermission !== "granted" && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-[11px] font-medium text-sky-600 hover:text-sky-700 hover:bg-sky-50 dark:hover:bg-sky-950/30 rounded-lg gap-1.5"
                onClick={handleRequestNotificationPermission}
                title={t("admin.notificationsDropdown.enableBrowserNotifications")}
              >
                <Settings className="w-3.5 h-3.5" />
                {t("admin.notificationsDropdown.enableShort")}
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={handleRefresh}
              disabled={loading}
              title={t("common.refresh")}
            >
              <RefreshCw
                className={cn(
                  "h-3.5 w-3.5 text-gray-500",
                  loading && "animate-spin"
                )}
              />
            </Button>

            {counts.total > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-[11px] font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg gap-1"
                onClick={handleMarkAllAsRead}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Tout lu
              </Button>
            )}
          </div>
        </div>

        {/* Category Tabs - Pill style */}
        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
          <Tabs
            value={activeCategory}
            onValueChange={(v) => changeCategory(v as AdminNotificationCategory)}
          >
            <TabsList className="flex gap-1.5 bg-transparent p-0 w-full justify-start">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.value}
                  value={cat.value}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all duration-200",
                    "data-[state=inactive]:bg-white dark:data-[state=inactive]:bg-gray-800 data-[state=inactive]:text-gray-600 dark:data-[state=inactive]:text-gray-400",
                    "data-[state=inactive]:border data-[state=inactive]:border-gray-200 dark:data-[state=inactive]:border-gray-700",
                    "data-[state=active]:bg-emerald-600 dark:data-[state=active]:bg-emerald-500 data-[state=active]:text-white dark:data-[state=active]:text-white",
                    "data-[state=active]:shadow-sm"
                  )}
                >
                  <span>{cat.label}</span>
                  {cat.count > 0 && (
                    <span
                      className={cn(
                        "ml-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center",
                        "text-[9px] font-bold rounded-full",
                        activeCategory === cat.value
                          ? "bg-white/20 text-inherit"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                      )}
                    >
                      {cat.count}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Notification List */}
        <ScrollArea className="h-[340px]">
          {loading ? (
            <LoadingSkeleton />
          ) : notifications.length === 0 ? (
            <EmptyState category={activeCategory} />
          ) : (
            <div className="p-2 space-y-1">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onNavigate={handleNavigate}
                  t={t}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="px-3 py-2.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-9 text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            onClick={() => handleNavigate("/notifications")}
          >
            Voir toutes les notifications
            <ExternalLink className="h-3 w-3 ml-1.5" />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AdminNotificationDropdown;
